import i18n, { DEFAULT_LANGUAGE } from "../../config/i18n";
import { ensureCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { OrganisationUid } from "../../interfaces";
import { gaPushEvent } from "./base.analytics";
import { congressContextProps } from "./domains/congress.analytics";
import type { CongressAttribution } from "../../domains/congress/congressAttribution";
import { getCurrentNavbarSection } from "./utils/getCurrentNavbarSection";
import { AnalyticsItemCategory, AnalyticsItemVariant } from "./analytics.types";

export enum BaseEvent {
  PAGE_VIEW = "page_view",
  SELECT_ITEM = "select_item",
  VIEW_ITEM = "view_item",
  VIEW_ITEM_LIST = "view_item_list",
  VIEW_ORIGINAL_PAPER = "view_original_paper",
  AUDIO_PLAYED = "audio_played",
  AUDIO_PROGRESS = "audio_progress",
  AUDIO_COMPLETE = "audio_complete",
}

export type AnalyticsItemReturnData = {
  item_id: string;
  item_name: string;
  item_brand: string | null;
  item_category: string | null;
  item_category2: AnalyticsItemCategory | null;
  item_variant: AnalyticsItemVariant;
  item_list_name?: string;
  quantity?: number;
  audio_duration_ms?: number;
  language?: string;
};

export async function gaEventPageView(
  room: PrivateRoom | null,
  eventParams: Record<string, unknown> = {}
) {
  const user = await ensureCurrentUser();

  const title = globalThis.location.pathname.replace("/", "");

  gaPushEvent(BaseEvent.PAGE_VIEW, {
    ...eventParams,
    environment: {
      entity: "web app",
      type: room ? "professional" : "public",
      partner: room?.organisationModel.uid ?? OrganisationUid.JUISCI,
      partner_id: room?.organisationModel.id ?? null,
      room_type: room ? (room.isPublic ? "public" : "professional") : null,
      room_id: room?.id ?? null,
    },
    page: {
      ...(typeof eventParams.page === "object" && eventParams.page !== null
        ? eventParams.page
        : {}),
      title,
      language: i18n.resolvedLanguage || DEFAULT_LANGUAGE,
      nav_bar_section: getCurrentNavbarSection(),
    },
    user: {
      ...(typeof eventParams.user === "object" && eventParams.user !== null
        ? eventParams.user
        : {}),
      account_id: user ? user.uid : null,
      profile: user ? (user.professionModel?.uid ?? null) : null,
      specialty: user ? (user.mainSpecialtyModel?.uid ?? null) : null,
      interests: user
        ? user.medicalSpecialtyModels
            .map((specialty) => specialty.uid)
            .toString()
        : null,
      login_status: user ? "logged in" : "logged out",
      internal: {
        organisation_uid: room?.organisationModel.uid ?? null,
        room_id: room?.id ?? null,
      },
    },
  });
}

export function gaEventSelectItem(item: unknown) {
  // TODO: Improve typing.
  gaPushEvent(BaseEvent.SELECT_ITEM, { ecommerce: { items: [item] } });
}

/**
 * `view_item` is where a content open becomes `content_viewed` /
 * `playlist_opened` in Mixpanel, and it fires on the DESTINATION page — so it
 * is the only place a congress-space open can be attributed.
 *
 * `congress` is REQUIRED rather than optional so every emitter states its
 * answer: five domains call this, and a default would have let one of them
 * silently stay unattributable. The props are spread CONDITIONALLY — the keys
 * must be absent, not null, outside a congress, or Mixpanel's "is set" filters
 * match every content open in the product.
 */
export function gaEventViewItem(
  item: unknown,
  congress: CongressAttribution | null
) {
  // TODO: Improve typing.
  gaPushEvent(BaseEvent.VIEW_ITEM, {
    ...(congress === null ? {} : congressContextProps(congress)),
    ecommerce: { items: [item] },
  });
}

export function gaEventViewItemList(
  items: unknown[],
  environmentParams?: Record<string, unknown>
) {
  // TODO: Improve typing.
  gaPushEvent(BaseEvent.VIEW_ITEM_LIST, {
    ...(environmentParams !== undefined
      ? { environment: environmentParams }
      : {}),
    ecommerce: { items },
  });
}
